import { PhotoIcon } from '@heroicons/react/24/outline';
import {
  Flex,
  Image,
  Radio,
  SimpleGrid,
  Space,
  Text,
  Title
} from '@mantine/core';
import { IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';

import { PageLoading } from '@/pages/PageLoading';
import type { ClubPhotoFormKey } from '@/pages/club/onboard/components/stages/photo/hooks/usePhotoPage';
import { usePhotoPage } from '@/pages/club/onboard/components/stages/photo/hooks/usePhotoPage';
import { resolveImagePath } from '@/shared/helper/resolveImagePath';
import { translate } from '@/shared/i18n/translate';
import { LBModalLayout } from '@/shared/layouts';
import { LBPhotoDropZone } from '@/shared/modules/photo-drop-zone';
import { LBButton } from '@/shared/ui-kit/components';

export const Photo = () => {
  const {
    state: { isLoading, form, isProfile, isSelectedProfilePhoto, isValidForm },
    functions: { onSubmit, onSelectProfilePhoto, onDeletePhoto }
  } = usePhotoPage();

  const [opened, { close, open }] = useDisclosure();
  const size = import.meta.env.VITE_PHOTO_MAX_SIZE as string;

  if (isLoading) return <PageLoading />;

  return (
    <>
      <LBModalLayout opened={opened} withCloseButton={false} onClose={close}>
        <Space h={4} />
        <Title order={2}>{translate('popups.mainPhoto.title')}</Title>
        <Space h={8} />
        <Text>{translate('popups.mainPhoto.description')}</Text>
        <Space h={32} />
        <Radio.Group value={isProfile} onChange={onSelectProfilePhoto}>
          <SimpleGrid cols={3}>
            {opened &&
              Object.entries(form.getValues()).map(([, value]) => (
                <Radio.Card key={value.tag} pos='relative' value={value.tag}>
                  <Radio.Indicator left={5} pos='absolute' top={5} />
                  <>
                    {typeof value.file === 'object' && (
                      <Image
                        src={URL.createObjectURL(value.file!)}
                        onLoad={() =>
                          URL.revokeObjectURL(URL.createObjectURL(value.file!))
                        }
                      />
                    )}

                    {typeof value.file === 'string' && (
                      <Image src={resolveImagePath(value.file)} />
                    )}
                  </>
                </Radio.Card>
              ))}
          </SimpleGrid>
        </Radio.Group>
        <Space h={32} />
        <LBButton fullWidth onClick={close}>
          {translate('commons.save')}
        </LBButton>
      </LBModalLayout>

      <form onSubmit={onSubmit}>
        <Title order={1}>
          {translate('pages.onboarding.club.photo.title')}
        </Title>
        <Space h={8} />
        <Text fz={14}>
          {translate('pages.onboarding.club.photo.description')}
        </Text>
        <Space h={32} />
        {/* 
        <LBPhotoDropZone
          accept={IMAGE_MIME_TYPE}
          disabled={form.submitting}
          maxFiles={5}
          initialValue={
            housingImages
              ? housingImages.map((item) => (item as PhotoItemDto).filePath)
              : []
          }
          onFilesChange={setHousingImages}
        >
          <Flex align='center' direction='column' py={70}>
            <PhotoIcon color='gray' width={64} />
            <Text c='blue' fz='sm'>
              {translate('commons.uploadManyFromDevice')}
            </Text>
            <Text c='blue' fz='sm' ta='center'>
              {translate('commons.photoParams', { size })}
            </Text>
          </Flex>
        </LBPhotoDropZone> */}

        {Object.entries(form.getValues()).map(([key, value], index) => (
          <div key={key}>
            <Text fz={14}>
              {index + 1}. {value.title}
            </Text>
            <Space h={4} />
            <LBPhotoDropZone
              accept={IMAGE_MIME_TYPE}
              maxFiles={1}
              initialValue={
                form.getValues()[key as ClubPhotoFormKey].file !== null
                  ? [`${form.getValues()[key as ClubPhotoFormKey].file}`]
                  : []
              }
              onDelete={() => onDeletePhoto(value.photoId, value.tag)}
              onFilesChange={(file) => {
                form.setFieldValue(`${key}.file`, file[0]);
              }}
            >
              <Flex align='center' direction='column' py={70}>
                <PhotoIcon color='gray' width={64} />
                <Text c='blue' fz='sm'>
                  {translate('commons.uploadFromDevice')}
                </Text>
                <Text fz={12} ta='center'>
                  {translate('commons.photoParams', { size })}
                </Text>
              </Flex>
            </LBPhotoDropZone>
            <Space h={16} />
          </div>
        ))}

        <Space h={32} />
        <LBButton
          fullWidth
          disabled={!isValidForm}
          variant='outline'
          onClick={open}
        >
          {translate('commons.selectMainPhoto')}
        </LBButton>
        <Space h={8} />
        <LBButton
          fullWidth
          disabled={!isSelectedProfilePhoto}
          loading={form.submitting}
          type='submit'
        >
          {translate('commons.save-and-continue')}
        </LBButton>
      </form>
    </>
  );
};
